import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box } from "@mui/material";
import { ReactNode } from "react";
import CustomTooltip from "./CustomTooltip";
import Text from "./Text";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "./app/provider/patient/chart";

const TOOLTIP_PADDING = "8px 12px";
const TOOLTIP_WIDTH = "260px";
const DEFAULT_ICON_LABEL = "More information";

export type InfoTooltipIconProps = {
  /** Hover-help body. A string is rendered as readable copy; nodes render as-is. */
  content: ReactNode;
  /** Optional heading above the body, e.g. the field this explains. */
  title?: string;
  /** Accessible name for the icon; defaults to a generic one. */
  ariaLabel?: string;
  width?: string;
};

/**
 * Info icon that reveals hover-help. `CustomTooltip` renders an unpadded white
 * surface for callers that supply their own card, so pad and colour the copy here.
 *
 * @author Rajkumar Rathod
 */
const InfoTooltipIcon = ({
  content,
  title,
  ariaLabel = DEFAULT_ICON_LABEL,
  width = TOOLTIP_WIDTH,
}: InfoTooltipIconProps) => (
  <CustomTooltip
    width={width}
    content={
      <Box sx={{ padding: TOOLTIP_PADDING }}>
        {!!title && (
          <Text
            {...CHART_TYPOGRAPHY.title6}
            fontWeight={CHART_TYPOGRAPHY.weight.semiBold}
            c={CHART_COLORS.textDark}
          >
            {title}
          </Text>
        )}
        <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textLabel}>
          {content}
        </Text>
      </Box>
    }
  >
    <InfoOutlinedIcon
      aria-label={ariaLabel}
      sx={{
        fontSize: CHART_DIMENSIONS.iconSm,
        color: CHART_COLORS.textMutedGrey,
        display: "block",
      }}
    />
  </CustomTooltip>
);

export default InfoTooltipIcon;
